import type { ReactNode } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { X } from 'lucide-react';

/**
 * A panel that slides in from the right: the detail of ONE item picked from a list (the design
 * standard's nav-slideouts). Never a list or a destination already on screen -- the Ways
 * Channels, Runners and Usage slideouts of 1.0.310 are views in the main area now (PO46-1649).
 *
 * Its surface follows the theme: AppShell puts `dark` on <html>, so the `dark:` classes here apply
 * even though Headless UI portals the dialog to <body>, outside the shell.
 *
 * Built to the HS design standard's dialog component: a Headless UI Dialog, so it is announced
 * (aria-modal, titled), focus is trapped inside and returns to the opener, Escape and the scrim
 * close it, and the close button is named. Motion is the standard's panel timing (200ms open,
 * 150ms close) and is dropped under prefers-reduced-motion.
 */
export default function WaysSlideover({ open, title, icon, onClose, children, width = 'w-[640px]' }: {
  open: boolean;
  title: string;
  icon?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** A Tailwind width class; clamped to the viewport either way. */
  width?: string;
}) {
  return (
    <Dialog open={open} onClose={onClose} className="relative z-40">
      <DialogBackdrop transition
        className="fixed inset-0 bg-black/20 transition-opacity duration-200 ease-out data-[closed]:opacity-0 data-[leave]:duration-150 data-[leave]:ease-in motion-reduce:transition-none" />
      <div className="fixed inset-y-0 right-0 flex max-w-full">
        <DialogPanel transition
          className={`${width} max-w-[100vw] h-screen bg-white dark:bg-gray-900 shadow-xl border-l border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden transition-transform duration-200 ease-out data-[closed]:translate-x-full data-[leave]:duration-150 data-[leave]:ease-in motion-reduce:transition-none`}>
          <div className="flex items-center justify-between px-4 h-12 border-b border-gray-200 dark:border-gray-700 shrink-0">
            <DialogTitle className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">{icon}{title}</DialogTitle>
            <button type="button" onClick={onClose} title="Close (Esc)" aria-label={`Close ${title}`}
              className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent">
              <X size={16} aria-hidden="true" />
            </button>
          </div>
          <div className="flex-1 min-h-0 flex flex-col">{children}</div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
